import { Base } from "ui/Scripts/source/framework/base";
import { IdTitle, IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { EnumHelper, ReportSharingType, ReportType, TaskDataRowDataType } from "ui/Scripts/source/models/enums";
import { ITaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ITaskEditTypeItem, TaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { IReportParameterValue, ReportParameterValue } from "./reportParameterValue";
import { ISavedReport } from "./savedReport";

export interface IReportItem extends IIdTitle {
    reportId: string;
    type: ReportType;
    sortOrder: number;
    taskType: ITaskEditTypeItem; //customer parameters schema
    taskDataRow: ITaskDataRow; //customer parameter values
    generalFilter: any; //User report parameter initialization
    reportFilter: any; //User report parameter initialization
    sortColumn: string; //User report parameter initialization
    sortOrderIsAsc: boolean; //User report parameter initialization

    orgTaskDataRow: ITaskDataRow;
    orgReportFilter: any;

    isUserReport: boolean;
    reportSharingType: ReportSharingType;

    showInFrontPage: boolean;

    getIsUserLevel(): boolean;
    getIsShared(): boolean;
    getTypeImageClass(): string;
    getTypeTitle(): string;
    getReportParameterValues(): IReportParameterValue[];
    copyReportParameterValuesFromSavedValues();
    copyReportParameterValues(report: IReportItem);
    resetTaskDataRowToOrg();
}

export class ReportItem extends IdTitle implements IReportItem {
    reportId: string;
    type: ReportType;
    sortOrder: number;
    taskType: TaskEditTypeItem; //customer parameters schema
    taskDataRow: TaskDataRow; //customer parameter values
    generalFilter: any; //User report parameter initialization
    reportFilter: any; //User report parameter initialization
    sortColumn: string; //User report parameter initialization
    sortOrderIsAsc: boolean; //User report parameter initialization

    orgTaskDataRow: TaskDataRow;
    orgReportFilter: any;

    isUserReport: boolean;
    reportSharingType: ReportSharingType;

    showInFrontPage: boolean;

    constructor();
    constructor(obj: IReportItem);
    constructor(obj?: any) {
        super(obj);
        this.reportId = obj && obj.reportId || "";
        this.type = obj && obj.type || ReportType.Html;
        this.sortOrder = obj && obj.sortOrder || 0;
        this.taskType = null;
        if (obj && obj.taskType) {
            this.taskType = new TaskEditTypeItem(obj.taskType);
        }
        this.taskDataRow = null;
        if (this.taskType) {
            if (!obj?.taskDataRow) {
                this.taskDataRow = new TaskDataRow();
                this.taskDataRow.id = Base.getGuid();
                this.taskDataRow.initializeToTaskType(this.taskType);
            } else {
                this.taskDataRow = new TaskDataRow(obj.taskDataRow);
                this.taskDataRow.initializeCols(this.taskType);
            }
            if (obj?.orgTaskDataRow) {
                this.orgTaskDataRow = new TaskDataRow(obj.orgTaskDataRow);
            } else {
                this.orgTaskDataRow = new TaskDataRow(this.taskDataRow);
            }
        }
        this.generalFilter = {};
        if (obj && obj.generalFilter) {
            this.generalFilter = obj.generalFilter;
        }
        this.reportFilter = {};
        if (obj && obj.reportFilter) {
            this.reportFilter = obj.reportFilter;
        }
        if (obj?.orgReportFilter) {
            this.orgReportFilter = obj.orgReportFilter;
        } else {
            this.orgReportFilter = this.reportFilter;
        }
        this.sortColumn = obj && obj.sortColumn || "";
        this.sortOrderIsAsc = obj && obj.sortOrderIsAsc || false;
        this.isUserReport = obj && obj.isUserReport || false;
        if (!this.isUserReport) {
            this.reportId = obj && obj.id || "";
        }
        this.reportSharingType = obj && obj.reportSharingType || ReportSharingType.NotShared;
        this.showInFrontPage = obj && obj.showInFrontPage || false;
    }

    getHash(): string {
        return super.getHash() + "#" + this.reportId + "#" + this.type.toString(10) + "#" +
            (this.taskType ? this.taskType.getHash() + "#" + JSON.stringify(this.taskType.formCols) : "") + "#" + (this.taskDataRow ? this.taskDataRow.getHash() : "") + "#" +
            JSON.stringify(this.generalFilter ?? {}) + "#" + JSON.stringify(this.reportFilter ?? {}) + "#" +
            this.sortColumn + "#" + this.sortOrderIsAsc.toString() + "#" + this.isUserReport.toString() + "#" + this.reportSharingType.toString(10) + "#" +
            this.showInFrontPage.toString();
    }

    getIsUserLevel(): boolean {
        return this.isUserReport;
    }
    
    getIsShared(): boolean {
        return this.reportSharingType > ReportSharingType.NotShared;
    }

    getTypeImageClass(): string {
        switch (this.type) {
        case ReportType.Html:
            return "file_type_8";
        case ReportType.Excel:
            return "file_type_2";
        case ReportType.Pdf:
            return "file_type_4";
        default:
            return "file_type_20";
        }
    }

    getTypeTitle(): string {
        switch (this.type) {
        case ReportType.Html:
            return "html";
        case ReportType.Excel:
            return "Excel";
        case ReportType.Pdf:
            return "pdf";
        default:
            return "";
        }
    }

    getReportParameterValues(): IReportParameterValue[] {
        if (!this.taskType || !this.taskDataRow) return [];
        const result: IReportParameterValue[] = [];
        for (const col of this.taskDataRow.cols) {
            const typeCol = this.taskType.formCols.find(i => i.field === col.field);
            if (!typeCol) continue;
            let value = this.taskDataRow.getTaskDataRowColUiValue(col.field, this.taskType);
            if (EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.StringArray) || EnumHelper.isEqual(typeCol.dataType, TaskDataRowDataType.TreeStringArray)) {
                const array: string[] = value ? JSON.parse(value) : [];
                value = JSON.stringify(array.filter(i => !!i));
            }
            result.push(ReportParameterValue.createReportParameterValue(col.field, value));
        }
        return result;
    }

    copyReportParameterValuesFromSavedValues() {
        if (!this.reportFilter || !this.taskType || !this.taskDataRow) return;
        for (const col of this.taskDataRow.cols) {
            const typeCol = this.taskType.formCols.find(i => i.field === col.field);
            if (!typeCol || !Object.prototype.hasOwnProperty.call(this.reportFilter, col.field)) continue;
            col.setTaskDataRowColValueFromDbValue(typeCol, this.reportFilter[col.field]);
        }
        if (this.taskType) {
            this.taskDataRow.initializeCols(this.taskType);
        }
    }

    copyReportParameterValues(report: IReportItem) {
        if (!report || !report.taskType || !report.taskDataRow) return;
        for (const col of this.taskDataRow.cols) {
            const typeCol = this.taskType.formCols.find(i => i.field === col.field);
            if (!typeCol) continue;
            const sourceTypeCol = report.taskType.formCols.find(i => i.field === col.field);
            if (!sourceTypeCol || !EnumHelper.isEqual(typeCol.dataType, sourceTypeCol.dataType)) continue;
            const sourceCol = report.taskDataRow.cols.find(i => i.field === col.field);
            if (!sourceCol) continue;
            col.valueStr = sourceCol.valueStr;
            col.valueNbr = sourceCol.valueNbr;
        }
    }

    resetTaskDataRowToOrg() {
        if (this.taskDataRow) {
            this.taskDataRow = new TaskDataRow(this.orgTaskDataRow);
            this.reportFilter = { ...this.orgReportFilter };
        }
    }

    static createReportItem(id: string, title: string, type: ReportType, taskType: ITaskEditTypeItem, generalFilter: any = null): IReportItem {
        const result = new ReportItem();
        result.id = id;
        result.reportId = id;
        result.title = title;
        result.type = type;
        result.taskType = taskType;
        result.generalFilter = generalFilter;
        return result;
    }

    static createReportItemFromSavedReport(report: IReportItem, savedReport: ISavedReport, reportSharingType: ReportSharingType): IReportItem {
        const result = new ReportItem(report);
        result.id = savedReport.id;
        result.reportId = report.reportId;
        result.title = savedReport.title;
        result.generalFilter = savedReport.generalFilter;
        result.reportFilter = savedReport.reportFilter;
        result.sortColumn = savedReport.sortColumn;
        result.sortOrderIsAsc = savedReport.sortColumn ? savedReport.sortOrderIsAsc : true;
        result.showInFrontPage = savedReport.showInFrontPage;
        result.isUserReport = true;
        result.reportSharingType = reportSharingType;
        result.orgReportFilter = result.reportFilter;
        result.copyReportParameterValuesFromSavedValues();
        result.orgTaskDataRow = new TaskDataRow(result.taskDataRow);
        return result;
    }

    static sortReportItems(items: IReportItem[]): IReportItem[] {
        if (!items && items.length < 2) return items;
        return items.sort((a: IReportItem, b: IReportItem) => {
            if (!a || !b) return 0;
            let result = Base.numCompare(a.isUserReport ? 0 : 1, b.isUserReport ? 0 : 1);
            if (result !== 0) return result;
            result = Base.numCompare(a.reportSharingType, b.reportSharingType);
            if (result !== 0) return result;
            result = Base.numCompare(a.type, b.type);
            if (result !== 0) return result;
            result = Base.numCompare(a.sortOrder, b.sortOrder);
            if (result !== 0) return result;
            return Base.strCompare(a.title, b.title);
        });
    }

    static sortReportItemsSharedAndStandard(items: IReportItem[]): IReportItem[] {
        if (!items && items.length < 2) return items;
        return items.sort((a: IReportItem, b: IReportItem) => {
            if (!a || !b) return 0;
            let result = Base.numCompare(a.type, b.type);
            if (result !== 0) return result;
            result = Base.numCompare(a.sortOrder, b.sortOrder);
            if (result !== 0) return result;
            return Base.strCompare(a.title, b.title);
        });
    }

    static getSharedAndStandardReports(standardReports: IReportItem[], savedReports: IReportItem[]): IReportItem[] {
        return ReportItem.sortReportItemsSharedAndStandard(standardReports.concat(savedReports.filter(i => i.reportSharingType > ReportSharingType.NotShared)));
    }

    static getItemsHash(standardReports: IReportItem[], savedReports: IReportItem[]): string {
        return standardReports.map(i => i.id).join(",") + savedReports.map(i => i.id + "#" + i.reportSharingType.toString(10)).join(",");
    };

    static getTaskTypeAndTaskDataRow(id: string, reports: IReportItem[]): { taskType: ITaskEditTypeItem; taskDataRow: ITaskDataRow; } {
        const result = {
            taskType: null,
            taskDataRow: null
        };
        if (!id) return result;
        const report = reports.find(i => i.id === id);
        if (report && report.taskType && report.taskDataRow && report.taskDataRow.cols.length > 0) {
            result.taskType = report.taskType;
            result.taskDataRow = report.taskDataRow;
        }
        return result;
    }

    static getActiveReport(state: { reports: IReportItem[], savedReports: IReportItem[], activeReportId: string, activeSavedReportId: string }): IReportItem {
        return state.activeReportId
            ? state.reports.find(i => i.id === state.activeReportId)
            : state.savedReports.find(i => i.id === state.activeSavedReportId);
    };
};
